import {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Search, ChevronUp, ChevronDown, LogIn } from 'lucide-react';
import {
  Input,
  Alert,
  AlertDescription,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Card,
  CardContent,
} from '@roger/ui-components';
import { getAdoptionReport } from '../../services/rogerDocApi';
import {
  type AdoptionReportEntry,
  toAdoptionReportEntry,
  getTotalEmrFilled,
  getTotalExternalCompletion,
  getPendingVisits,
  getRogerVsExternalPercentage,
} from '../../types/adoptionReport.types';
import { getClinicianFullName } from '../../types/clinician.types';
import {
  getPendingVisits as getVisitPendingVisits,
  getRogerVsExternalPercentage as getVisitRogerVsExternalPercentage,
} from '../../types/visitCompletion.types';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import PageLoader from '../PageLoader';

export interface VisitCompletionByClinicianRef {
  exportToCSV: () => void;
}

export interface VisitCompletionByClinicianProps {
  onExportRegistry?: (exportFunc: (() => void) | null) => void;
}

// Sortable header component
const SortableHeader = ({
  column,
  children,
  sortColumn,
  sortDirection,
  onSort,
}: {
  column: string;
  children: React.ReactNode;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
}) => (
  <th
    className='cursor-pointer select-none bg-muted border-b-2 border-border p-3 text-left font-semibold text-sm hover:bg-muted/80 transition-colors'
    onClick={() => onSort(column)}
  >
    <div className='flex items-center justify-between pointer-events-none'>
      <span className='font-semibold'>{children}</span>
      {sortColumn === column && sortDirection === 'asc' ? (
        <ChevronUp className='h-4 w-4' />
      ) : sortColumn === column && sortDirection === 'desc' ? (
        <ChevronDown className='h-4 w-4' />
      ) : (
        <ChevronDown className='h-4 w-4 opacity-30' />
      )}
    </div>
  </th>
);

const VisitCompletionByClinician = forwardRef<
  VisitCompletionByClinicianRef,
  VisitCompletionByClinicianProps
>(({ onExportRegistry }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('rogerPercentage');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [reportData, setReportData] = useState<AdoptionReportEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(() => {
    const saved = localStorage.getItem('adoptionReport_showBreakdown');
    return saved ? JSON.parse(saved) : false;
  });
  const [hideNeverLoggedIn, setHideNeverLoggedIn] = useState(() => {
    const saved = localStorage.getItem('adoptionReport_hideNeverLoggedIn');
    return saved ? JSON.parse(saved) : false;
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  // Fetch data from API on component mount and when dates change
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getAdoptionReport(fromDate, toDate);
        const processedData = data.map(rawData =>
          toAdoptionReportEntry(rawData)
        );
        setReportData(processedData);
        showSuccess(`Loaded ${processedData.length} clinician records`);
      } catch (err) {
        console.error('Error loading adoption report:', err);
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        showError(`Error loading report: ${errorMessage}`);
        setReportData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [fromDate, toDate]);

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      // Default to desc for numeric columns, asc for text
      if (
        ['pending', 'roger', 'external', 'rogerPercentage'].includes(column)
      ) {
        setSortDirection('desc');
      } else {
        setSortDirection('asc');
      }
    }
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = reportData;

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => {
        const fullName = getClinicianFullName(entry.clinician).toLowerCase();
        const firstName = entry.clinician.firstName.toLowerCase();
        const lastName = entry.clinician.lastName.toLowerCase();
        const title = (entry.clinician.title || '').toLowerCase();

        return (
          fullName.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          title.includes(searchLower)
        );
      });
    }

    // Apply hasLoggedIn filter
    if (hideNeverLoggedIn) {
      filtered = filtered.filter(entry => entry.clinician.hasLoggedIn);
    }

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortColumn) {
          case 'name':
            aValue =
              `${a.clinician.lastName}, ${a.clinician.firstName}`.toLowerCase();
            bValue =
              `${b.clinician.lastName}, ${b.clinician.firstName}`.toLowerCase();
            break;
          case 'title':
            aValue = (a.clinician.title || '').toLowerCase();
            bValue = (b.clinician.title || '').toLowerCase();
            break;
          case 'pending':
            aValue = getPendingVisits(a);
            bValue = getPendingVisits(b);
            break;
          case 'roger':
            aValue = getTotalEmrFilled(a);
            bValue = getTotalEmrFilled(b);
            break;
          case 'external':
            aValue = getTotalExternalCompletion(a);
            bValue = getTotalExternalCompletion(b);
            break;
          case 'rogerPercentage':
            aValue =
              getTotalEmrFilled(a) === 0 && getTotalExternalCompletion(a) === 0
                ? 0
                : getTotalEmrFilled(a) /
                  (getTotalEmrFilled(a) + getTotalExternalCompletion(a));
            bValue =
              getTotalEmrFilled(b) === 0 && getTotalExternalCompletion(b) === 0
                ? 0
                : getTotalEmrFilled(b) /
                  (getTotalEmrFilled(b) + getTotalExternalCompletion(b));
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue as string)
            : (bValue as string).localeCompare(aValue);
        } else {
          return sortDirection === 'asc'
            ? aValue - (bValue as number)
            : (bValue as number) - aValue;
        }
      });
    }

    return filtered;
  }, [reportData, searchTerm, sortColumn, sortDirection, hideNeverLoggedIn]);

  // Export to CSV function
  const exportToCSV = () => {
    const escapeCsvValue = (value: unknown) => {
      if (value == null) return '';
      const stringValue = String(value);
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headers = [
      'Clinician Name (Last, First)',
      'Title',
      'Has Logged In',
      'Pending Visits',
      'Pending Visits - OASIS',
      'Pending Visits - Regular',
      'Completed in Roger',
      'Completed in Roger - OASIS',
      'Completed in Roger - Regular',
      'Completed Outside of Roger',
      'Completed Outside of Roger - OASIS',
      'Completed Outside of Roger - Regular',
      '% Completed in Roger',
      '% Completed in Roger - OASIS',
      '% Completed in Roger - Regular',
    ];

    const csvRows = reportData.map(entry => {
      const totalPercentage = getRogerVsExternalPercentage(entry) + '%';

      return [
        escapeCsvValue(getClinicianFullName(entry.clinician)),
        escapeCsvValue(entry.clinician.title || ''),
        escapeCsvValue(entry.clinician.hasLoggedIn ? 'Yes' : 'No'),
        escapeCsvValue(getPendingVisits(entry)),
        escapeCsvValue(getVisitPendingVisits(entry.oasis)),
        escapeCsvValue(getVisitPendingVisits(entry.regular)),
        escapeCsvValue(getTotalEmrFilled(entry)),
        escapeCsvValue(entry.oasis.emrFilled),
        escapeCsvValue(entry.regular.emrFilled),
        escapeCsvValue(getTotalExternalCompletion(entry)),
        escapeCsvValue(entry.oasis.externalCompletion),
        escapeCsvValue(entry.regular.externalCompletion),
        escapeCsvValue(totalPercentage),
        escapeCsvValue(getVisitRogerVsExternalPercentage(entry.oasis) + '%'),
        escapeCsvValue(getVisitRogerVsExternalPercentage(entry.regular) + '%'),
      ].join(',');
    });

    const csvContent = [headers.map(escapeCsvValue).join(','), ...csvRows].join(
      '\n'
    );

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `roger_adoption_report_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Expose exportToCSV method via ref
  useImperativeHandle(ref, () => ({
    exportToCSV,
  }));

  // Register export function
  useEffect(() => {
    if (onExportRegistry) {
      if (loading || reportData.length === 0) {
        onExportRegistry(null);
      } else {
        onExportRegistry(exportToCSV);
      }
    }
  }, [loading, reportData.length, onExportRegistry]);

  if (loading) {
    return <PageLoader message='Loading adoption report data...' />;
  }

  return (
    <>
      {/* Error State */}
      {error && (
        <Alert variant='destructive'>
          <AlertDescription>
            Error loading adoption report: {error}
          </AlertDescription>
        </Alert>
      )}

      <div className='text-sm text-[#735bd6] mb-2 -mt-2'>
        All data is from 08/25/2025 onward
      </div>

      {/* Search and Filter Controls */}
      <Card>
        <CardContent className='p-4'>
          <div className='flex gap-4 items-end flex-wrap'>
            <div className='relative flex-1 flex-grow'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                type='text'
                placeholder='Search by clinician name...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='pl-10'
              />
            </div>

            <div className='flex flex-col gap-1'>
              <label htmlFor='fromDate' className='text-xs font-medium'>
                From Date
              </label>
              <Input
                id='fromDate'
                type='date'
                value={fromDate ? fromDate.toISOString().split('T')[0] : ''}
                onChange={e => {
                  setFromDate(e.target.value ? new Date(e.target.value) : null);
                }}
              />
            </div>

            <div className='flex flex-col gap-1'>
              <label htmlFor='toDate' className='text-xs font-medium'>
                To Date
              </label>
              <Input
                id='toDate'
                type='date'
                value={toDate ? toDate.toISOString().split('T')[0] : ''}
                onChange={e => {
                  setToDate(e.target.value ? new Date(e.target.value) : null);
                }}
              />
            </div>

            <div className='flex flex-col'>
              <div className='flex items-center gap-2'>
                <label className='flex items-center space-x-2'>
                  <Checkbox
                    checked={showBreakdown}
                    onCheckedChange={checked => {
                      setShowBreakdown(!!checked);
                      localStorage.setItem(
                        'adoptionReport_showBreakdown',
                        JSON.stringify(!!checked)
                      );
                    }}
                  />
                  <span className='text-sm'>Display visit type breakdown</span>
                </label>
              </div>

              <div className='flex items-center gap-2'>
                <label className='flex items-center space-x-2'>
                  <Checkbox
                    checked={hideNeverLoggedIn}
                    onCheckedChange={checked => {
                      setHideNeverLoggedIn(!!checked);
                      localStorage.setItem(
                        'adoptionReport_hideNeverLoggedIn',
                        JSON.stringify(!!checked)
                      );
                    }}
                  />
                  <span className='text-sm'>
                    Hide users who never logged in
                  </span>
                </label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className='p-0'>
          <div className='text-xs text-muted-foreground px-4 pt-3 pb-2'>
            Showing {filteredAndSortedData.length} of {reportData.length}{' '}
            clinicians
          </div>
          <div className='overflow-auto max-h-[600px]'>
            <table className='w-full border-collapse'>
              <thead className='sticky top-0 z-10 bg-background shadow-sm'>
                <tr>
                  <SortableHeader
                    column='name'
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Clinician Name
                  </SortableHeader>
                  <SortableHeader
                    column='title'
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Title
                  </SortableHeader>
                  <SortableHeader
                    column='pending'
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Pending Visits
                  </SortableHeader>
                  <SortableHeader
                    column='roger'
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Completed in Roger
                  </SortableHeader>
                  <SortableHeader
                    column='external'
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Completed Outside of Roger
                  </SortableHeader>
                  <SortableHeader
                    column='rogerPercentage'
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    % Completed in Roger
                  </SortableHeader>
                </tr>
              </thead>

              <tbody>
                {filteredAndSortedData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className='p-8 text-center text-muted-foreground'
                    >
                      No clinicians found matching your filters.
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedData.map((entry, index) => {
                    const totalPercentage =
                      getRogerVsExternalPercentage(entry) + '%';

                    return (
                      <tr
                        key={`${entry.clinicianUsername}-${index}`}
                        className='hover:bg-muted/50 border-b border-border'
                      >
                        <td className='p-3'>
                          <div className='flex items-center justify-between'>
                            <span className='font-medium'>
                              {getClinicianFullName(entry.clinician)}
                            </span>
                            {!entry.clinician.hasLoggedIn && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <LogIn className='h-4 w-4 text-destructive cursor-help' />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      This user has never logged into the app
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </td>
                        <td className='p-3'>
                          <span className='text-sm'>
                            {entry.clinician.title || '-'}
                          </span>
                        </td>
                        <td className='p-3'>
                          {showBreakdown ? (
                            <div className='flex items-center gap-2'>
                              <span className='font-medium'>
                                {getPendingVisits(entry)}
                              </span>
                              <div className='text-xs text-muted-foreground'>
                                <div>
                                  {getVisitPendingVisits(entry.oasis)} OASIS
                                </div>
                                <div>
                                  {getVisitPendingVisits(entry.regular)} Regular
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className='font-medium'>
                              {getPendingVisits(entry)}
                            </span>
                          )}
                        </td>
                        <td className='p-3'>
                          {showBreakdown ? (
                            <div className='flex items-center gap-2'>
                              <span className='font-medium'>
                                {getTotalEmrFilled(entry)}
                              </span>
                              <div className='text-xs text-muted-foreground'>
                                <div>{entry.oasis.emrFilled} OASIS</div>
                                <div>{entry.regular.emrFilled} Regular</div>
                              </div>
                            </div>
                          ) : (
                            <span className='font-medium'>
                              {getTotalEmrFilled(entry)}
                            </span>
                          )}
                        </td>
                        <td className='p-3'>
                          {showBreakdown ? (
                            <div className='flex items-center gap-2'>
                              <span className='font-medium'>
                                {getTotalExternalCompletion(entry)}
                              </span>
                              <div className='text-xs text-muted-foreground'>
                                <div>
                                  {entry.oasis.externalCompletion} OASIS
                                </div>
                                <div>
                                  {entry.regular.externalCompletion} Regular
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className='font-medium'>
                              {getTotalExternalCompletion(entry)}
                            </span>
                          )}
                        </td>
                        <td className='p-3'>
                          {showBreakdown ? (
                            <div className='flex items-center gap-2'>
                              <span className='font-medium'>
                                {totalPercentage}
                              </span>
                              <div className='text-xs text-muted-foreground'>
                                <div>
                                  {getVisitRogerVsExternalPercentage(
                                    entry.oasis
                                  )}
                                  % OASIS
                                </div>
                                <div>
                                  {getVisitRogerVsExternalPercentage(
                                    entry.regular
                                  )}
                                  % Regular
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className='font-medium'>
                              {totalPercentage}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
});

VisitCompletionByClinician.displayName = 'VisitCompletionByClinician';

export default VisitCompletionByClinician;
